import CloudDownloadRoundedIcon from '@mui/icons-material/CloudDownloadRounded';
import ReceiptIcon from '@mui/icons-material/Receipt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { Tooltip, IconButton } from '@mui/material';
import { GridActionsCellItem, type GridColDef } from '@mui/x-data-grid';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { downloadFile, fetchFileAsBlob } from '../../lib/services/blob-service';
import {
  checkTallyConnection,
  importVouchersToTally,
} from '../../lib/tally/tallyExport';
import { readExcelFile } from '../../lib/tally/tally.excel';
import { parseExcelData } from '../../lib/tally/tally.parser';
import { convertToXML } from '../../lib/tally/tally.xml';
import { updateEntity } from '../../lib/services/api-service';
import { toast } from 'react-toastify';
import type { IGridData } from './index';

export const columns = (refetchData: () => Promise<void>): GridColDef[] => {
  const onFileDownload = async (item: IGridData) => {
    try {
      const fileDownloadPath = `${format(item.ExportDate, 'yyyy/MM/dd')}/${item.BatchId}/result-${item.BatchId}.xlsx`;
      await downloadFile(fileDownloadPath);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download file');
    }
  };

  const handlePostToTally = async (item: IGridData) => {
    try {
      // Step 1: Check Tally connection
      const connectionStatus = await checkTallyConnection();
      if (connectionStatus.isConnected) {
        toast.success('Successfully connected to Tally');
      }
      if (!connectionStatus.isConnected) {
        toast.error(connectionStatus.message || 'Unable to connect to Tally');
        return;
      }

      // Step 2: Download the Excel file
      const fileDownloadPath = `${format(item.ExportDate, 'yyyy/MM/dd')}/${item.BatchId}/result-${item.BatchId}.xlsx`;
      const blob = await fetchFileAsBlob(fileDownloadPath);

      // Step 3: Prepare the file
      const file = new File([blob], `result-${item.BatchId}.xlsx`, {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      // Step 4: Read Excel file
      const excelData = await readExcelFile(file);

      if (!excelData || excelData.length === 0) {
        toast.error('Excel file is empty or contains no valid data');
        return;
      }

      // Step 5: Parse Excel data
      const tallyEntries = parseExcelData(excelData);

      if (tallyEntries.length === 0) {
        toast.error('File is Empty or Invalid');
        return;
      }

      // Step 6: Convert to XML
      const xmlData = convertToXML(tallyEntries);

      // Step 7: Import to Tally and handle response
      const tallyResponse = await importVouchersToTally(xmlData);

      // Step 8: Update entity status if successful
      if (tallyResponse.success) {
        try {
          await updateEntity('JobStatus', {
            PartitionKey: item.PartitionKey,
            RowKey: item.RowKey,
            PostToTally: true,
            TallyResponse: JSON.stringify(tallyResponse.parsedResponse),
          });

          // Refresh the data
          await refetchData();
        } catch (updateError) {
          console.error('Error updating entity:', updateError);
          toast.warning('Data imported to Tally, but failed to update status', {
            autoClose: 8000,
          });
        }
      }
    } catch (error) {
      console.error('Error posting to Tally:', error);
      toast.error(
        `Tally import failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );
    }
  };

  // Helper function to check if Tally response is successful
  const isTallyResponseSuccessful = (response: Record<string, any>) => {
    try {
      if (!response || typeof response !== 'object') {
        return false;
      }

      // Consider successful if at least one entry was created
      const created = parseInt(response.CREATED || '0');
      return created > 0;
    } catch (error) {
      console.error('Error checking Tally response:', error);
      return false;
    }
  };

  return [
    {
      field: 'BatchId',
      headerName: 'Batch ID',
      valueGetter: (value: string) => value,
      renderCell: ({ value }) => (
        <Link to={'/batch/' + value}>
          <div className="text-blue-400">{value}</div>
        </Link>
      ),
      flex: 1,
      headerClassName: 'font-extrabold text-xl text-black-900',
      filterable: true,
      sortable: true,
      width: 250,
    },
    {
      field: 'ExportDate',
      headerName: 'Exported Date',
      valueFormatter: params => format(params ?? new Date(), 'PPp'),
      flex: 1,
      cellClassName: 'font-semibold',
      headerClassName: 'font-extrabold text-xl text-black-900',
      sortable: true,
      filterable: true,
      width: 350,
    },
    {
      field: 'Status',
      headerName: 'Status',
      width: 150,
      renderCell: ({ row }) => (
        <div className={`${statusColor(row.Status)} font-semibold`}>
          {statusText(row.Status)}
        </div>
      ),
      sortable: true,
      headerClassName: 'font-extrabold text-xl text-black-900',
    },
    {
      field: 'FileCount',
      headerName: 'File Count',
      renderCell: ({ value, row }) => (
        <div>
          <Tooltip
            title={row.Files.map(
              (file: { fileName: string }) => file.fileName,
            ).join(', ')}>
            <div className="text-gray-900 font-semibold">{value}</div>
          </Tooltip>
        </div>
      ),
      sortable: true,
      width: 150,
      headerClassName: 'font-extrabold text-xl text-black-900',
    },
    {
      field: 'CreatedBy',
      valueGetter: (value: string) => value,
      headerName: 'Processed By',
      width: 200,
      cellClassName: 'font-semibold',
      headerClassName: 'font-extrabold text-xl text-black-900',
      sortable: true,
      filterable: true,
    },
    {
      field: 'actions',
      headerName: 'Download',
      type: 'actions',
      width: 200,
      getActions: ({ row }) => [
        <GridActionsCellItem
          key={row.BatchId}
          icon={<CloudDownloadRoundedIcon color="primary" />}
          label="Download"
          onClick={() => onFileDownload(row)}
        />,
      ],
      headerClassName: 'font-extrabold text-xl text-black-900',
    },
    {
      field: 'PostToTally',
      headerName: 'Post To Tally',
      width: 150,
      align: 'center',
      renderCell: ({ row }) => (
        <Tooltip
          title={
            row.PostToTally === true ? 'Posted to Tally' : 'Post to Tally'
          }>
          <span>
            <IconButton
              onClick={() => handlePostToTally(row)}
              disabled={row.PostToTally === true}
              color="primary">
              <ReceiptIcon />
            </IconButton>
          </span>
        </Tooltip>
      ),
      headerClassName: 'font-extrabold text-xl text-black-900',
    },
    {
      field: 'TallyResponse',
      headerName: 'Tally Response',
      align: 'center',
      width: 200,
      renderCell: ({ row }) => {
        let tallyResponseData = null;
        try {
          if (row.TallyResponse) {
            tallyResponseData = JSON.parse(row.TallyResponse);
          }
        } catch (e) {
          tallyResponseData = null;
        }

        const isSuccess = isTallyResponseSuccessful(tallyResponseData);

        // Generate formatted response text
        const generateFormattedResponse = () => {
          if (!tallyResponseData) return 'No response data';

          return [
            `Batch ID: ${row.BatchId}`,
            `Entry Created: ${tallyResponseData.CREATED || '0'}`,
            `Exception: ${tallyResponseData.EXCEPTIONS || '0'}`,
            tallyResponseData.LINEERROR
              ? `Line Error: ${tallyResponseData.LINEERROR}`
              : '',
          ]
            .filter(Boolean)
            .join('\n');
        };

        // Handle download of response as text file
        const handleDownload = (event: { stopPropagation: () => void }) => {
          event.stopPropagation();

          const content = generateFormattedResponse();
          const blob = new Blob([content], { type: 'text/plain' });
          const url = URL.createObjectURL(blob);

          const link = document.createElement('a');
          link.href = url;
          link.download = `tally-response-${row.BatchId}.txt`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        };

        // Create tooltip content based on the formatted response
        const tooltipContent = generateFormattedResponse();

        return (
          <div className="flex items-center">
            <Tooltip title={tooltipContent}>
              {row.PostToTally ? (
                isSuccess ? (
                  <CheckCircleIcon color="success" />
                ) : (
                  <ErrorIcon color="error" />
                )
              ) : (
                <CheckCircleIcon color="disabled" />
              )}
            </Tooltip>

            {row.PostToTally && (
              <Tooltip title="Download Response">
                <IconButton
                  size="small"
                  onClick={handleDownload}
                  disabled={!tallyResponseData}>
                  <CloudDownloadRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </div>
        );
      },
      headerClassName: 'font-extrabold text-xl text-black-900',
    },
  ];
};

const statusColor = (status: string) => {
  switch (status.toLowerCase()) {
    case 'success':
      return 'text-green-600';
    case 'partial':
      return 'text-orange-500';
    case 'failed':
      return 'text-red-600';
    case 'processing':
      return 'text-gray-400';
    default:
      return 'text-gray-400';
  }
};

const statusText = (status: string) => {
  switch (status.toLowerCase()) {
    case 'success':
      return 'Success';
    case 'partial':
      return 'Partially Successful';
    case 'failed':
      return 'Failed';
    case 'processing':
      return 'Processing';
    default:
      return 'Unknown';
  }
};

export default columns;
